import { create } from "zustand"

import type { ProductInventoriesSearchParams } from "../schemas"

import {
  GetProductInventoryListDocument,
  type GetProductInventoryListQuery,
  type InventoryByProductItem,
} from "@/graphql/codegen/graphql"
import { createSearchStore } from "@/stores/createSearchStore"

const deduplicateInventories = (
  newData: InventoryByProductItem[],
  existingData: InventoryByProductItem[]
): InventoryByProductItem[] => {
  const seen = new Set(existingData.map((item) => item.product.productId))
  return [...existingData, ...newData.filter((item) => !seen.has(item.product.productId))]
}

export const useProductInventorySearchStore = create(
  createSearchStore<InventoryByProductItem, ProductInventoriesSearchParams, GetProductInventoryListQuery>({
    documentNode: GetProductInventoryListDocument,
    deduplicateData: deduplicateInventories,
    getQueryInput: (filters: ProductInventoriesSearchParams, offset: number, limit: number) => {
      const { field, order, query, productStatuses, productManagerIds, productClassIds, warehouseAreaId } = filters
      const isActive = productStatuses?.length === 1 ? productStatuses[0] === "ACTIVE" : undefined

      return {
        offset,
        limit,
        sort: field && order ? { field, order } : undefined,
        filter: {
          query,
          isActive,
          productManagerIds,
          productClassIds,
          warehouseIds: warehouseAreaId ? [warehouseAreaId] : undefined,
        },
      }
    },
    getQueryVariables: (queryInput) => ({ input: queryInput }),
    extractDataFromResponse: (data) => ({
      __typename: data.inventory.listByProduct.__typename,
      data:
        data.inventory.listByProduct.__typename === "InventoryByProductListSuccess"
          ? (data.inventory.listByProduct.items as InventoryByProductItem[])
          : [],
      error:
        data.inventory.listByProduct.__typename === "InventoryByProductListFailure"
          ? data.inventory.listByProduct.error
          : undefined,
    }),
    hasMoreData: (response, state) => response.data.length === state.limit,
  })
)
